import { useState, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useDataSourceQuery } from '@/hooks/useDataSources'
import { useDropOperation } from '@/hooks/useDropOperation'
import { useBulkDropOperation } from '@/hooks/useBulkDropOperation'
import { databaseKeys } from '@/hooks/useDatabase'
import { Allotment } from 'allotment'
import 'allotment/dist/style.css'
import { DatabaseTree, type DatabaseTreeRef, type RefreshData } from '@/components/explorer/DatabaseTree'
import { TableDataView } from '@/components/explorer/TableDataView'
import { TableSizeView } from '@/components/explorer/TableSizeView'
import { TableEditor } from '@/components/explorer/TableEditor'
import { IndexesView } from '@/components/explorer/IndexesView'
import { TablesView } from '@/components/explorer/TablesView'
import { ViewsView } from '@/components/explorer/ViewsView'
import { FunctionsView } from '@/components/explorer/FunctionsView'
import { ProceduresView } from '@/components/explorer/ProceduresView'
import { TriggersView } from '@/components/explorer/TriggersView'
import { SequencesView } from '@/components/explorer/SequencesView'
import { ExtensionsView } from '@/components/explorer/ExtensionsView'
import { EventsView } from '@/components/explorer/EventsView'
import { ProcedureDetailsView } from '@/components/explorer/ProcedureDetailsView'
import { FunctionDetailsView } from '@/components/explorer/FunctionDetailsView'
import { IndexDetailsView } from '@/components/explorer/IndexDetailsView'
import { TriggerDetailsView } from '@/components/explorer/TriggerDetailsView'
import { SequenceDetailsView } from '@/components/explorer/SequenceDetailsView'
import { EventDetailsView } from '@/components/explorer/EventDetailsView'
import { ExtensionDetailsView } from '@/components/explorer/ExtensionDetailsView'
import { ViewDetailsView } from '@/components/explorer/ViewDetailsView'
import { DatabaseDetailsView } from '@/components/explorer/DatabaseDetailsView'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { CreateTableView } from '@/components/explorer/CreateTableView'
import { CreateDatabaseView } from '@/components/explorer/CreateDatabaseView'
import { CreateIndexView } from '@/components/explorer/CreateIndexView'
import { CreateTriggerView } from '@/components/explorer/CreateTriggerView'
import { CreateFunctionView } from '@/components/explorer/CreateFunctionView'
import { CreateSequenceView } from '@/components/explorer/CreateSequenceView'
import { CreateViewView } from '@/components/explorer/CreateViewView'
import { CreateEventView } from '@/components/explorer/CreateEventView'
import { CreateExtensionView } from '@/components/explorer/CreateExtensionView'
import { CreateProcedureView } from '@/components/explorer/CreateProcedureView'
import { CreateSchemaView } from '@/components/explorer/CreateSchemaView'
import { DatabaseDiagramModal } from '@/components/explorer/DatabaseDiagramModal'
import { ExportModal, type ExportOptions } from '@/components/explorer/ExportModal'
import { AgentProxyService } from '@/services/agentProxyService'
import { PlaceholderModal } from '@/components/explorer/PlaceholderModal'
import { DropConfirmDialog } from '@/components/explorer/DropConfirmDialog'
import { BulkDropConfirmDialog } from '@/components/explorer/BulkDropConfirmDialog'
import { useToast } from '@/hooks/useToast'
import type { ContextMenuActionData } from '@/components/explorer/contextMenuBuilders'
import { generateFunctionCall, generateProcedureCall, navigateToQueryEditor } from '@/utils/functionExecution'


export interface SelectedItem {
  type: 'connection' | 'database' | 'table' | 'view' | 'function' | 'procedure' | 'schema' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event' | 'indexes-folder' | 'tables-folder' | 'views-folder' | 'functions-folder' | 'procedures-folder' | 'triggers-folder' | 'sequences-folder' | 'extensions-folder' | 'events-folder' | 'schemas-folder' | 'create-database' | 'create-table' | 'create-index' | 'create-trigger' | 'create-function' | 'create-sequence' | 'create-view' | 'create-procedure' | 'create-event' | 'create-extension' | 'create-schema' | 'folder' | 'databases-root'
  connectionId: string
  database?: string
  schema?: string
  name: string
  fullPath?: string
  table?: string // For create-index when triggered from a specific table
  // Additional properties used in context menu actions
  user?: string
  trigger?: string
  arguments?: string // For function overloading
  sequence?: string
  extension?: string
  selectedItems?: SelectedItem[]
  folderType?: string
  duplicateFrom?: {
    originalName: string
    newName: string
    definition: unknown
  }
}



type ViewMode = 'explorer' | 'table-sizes'

export function DatabaseExplorer() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: dataSource, isLoading: isLoadingConnection } = useDataSourceQuery(connectionId || '')
  const { showToast } = useToast()

  const databaseTreeRef = useRef<DatabaseTreeRef>(null)
  
  // Drop operations hook
  const {
    isDropDialogOpen,
    dropData,
    isDropLoading,
    initiateDropOperation,
    confirmDrop,
    cancelDrop
  } = useDropOperation({
    onSuccess: (data) => {
      // Clear selected item if the dropped database matches the currently selected database
      if (data.type === 'database' && selectedItem?.type === 'database') {
        if (data.name === selectedItem.database || data.name === selectedItem.name) {
          setSelectedItem(null)
        }
      }
      // Also clear selected item if it belongs to the dropped database
      else if (data.type === 'database' && selectedItem?.database === data.name) {
        setSelectedItem(null)
      }
      
      // Clear database expansion state to prevent further queries
      if (data.type === 'database' && databaseTreeRef.current) {
        databaseTreeRef.current.clearDatabaseExpansion(data.name)
      }
      
      // Navigate to views page after dropping a view
      if (data.type === 'view') {
        setSelectedItem({
          type: 'views-folder',
          connectionId: data.connectionId,
          database: data.database,
          schema: data.schema,
          name: 'Views',
          fullPath: data.schema ? `${data.database}.${data.schema}.Views` : `${data.database}.Views`
        })
      }
      
      // Clear multi-selection after successful single drop
      setClearMultiSelection(true)
      // Reset the flag after a short delay to allow the effect to trigger
      setTimeout(() => { setClearMultiSelection(false) }, 100)
    }
  })
  
  // Bulk drop operations hook
  const {
    isBulkDropDialogOpen,
    bulkDropData,
    isBulkDropLoading,
    progress,
    initiateBulkDropOperation,
    confirmBulkDrop,
    cancelBulkDrop
  } = useBulkDropOperation({
    onSuccess: (result) => {
      // Clear selected item if any deleted databases match the currently selected database
      if (selectedItem?.type === 'database') {
        const deletedDatabases = result.successful.filter(item => item.type === 'database')
        const isSelectedDatabaseDeleted = deletedDatabases.some(item => 
          item.name === selectedItem.database || item.name === selectedItem.name
        )
        if (isSelectedDatabaseDeleted) {
          setSelectedItem(null)
        }
      }
      // Also clear selected item if it belongs to a deleted database
      else if (selectedItem?.database) {
        const deletedDatabases = result.successful.filter(item => item.type === 'database')
        const isSelectedItemInDeletedDatabase = deletedDatabases.some(item => 
          item.name === selectedItem.database
        )
        if (isSelectedItemInDeletedDatabase) {
          setSelectedItem(null)
        }
      }
      
      // Clear database expansion state for all dropped databases
      if (databaseTreeRef.current) {
        const deletedDatabases = result.successful.filter(item => item.type === 'database')
        deletedDatabases.forEach(database => {
          databaseTreeRef.current!.clearDatabaseExpansion(database.name)
        })
      }
      
      // Navigate to views page after bulk dropping views
      const deletedViews = result.successful.filter(item => item.type === 'view')
      if (deletedViews.length > 0 && connectionId) {
        // Use the first deleted view's database and schema for navigation
        const [firstView] = deletedViews
        setSelectedItem({
          type: 'views-folder',
          connectionId: connectionId || '',
          database: firstView.database,
          schema: firstView.schema,
          name: 'Views',
          fullPath: firstView.schema ? `${firstView.database}.${firstView.schema}.Views` : `${firstView.database}.Views`
        })
      }
      
      // Clear multi-selection after successful bulk drop
      setClearMultiSelection(true)
      // Reset the flag after a short delay to allow the effect to trigger
      setTimeout(() => { setClearMultiSelection(false) }, 100)
    }
  })
  
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('explorer')
  const [clearMultiSelection, setClearMultiSelection] = useState<boolean>(false)
  const [isEditingTable, setIsEditingTable] = useState(false)
  const [isEditingView, setIsEditingView] = useState(false)
  const [isViewEditPanelOpen, setIsViewEditPanelOpen] = useState(false)  // Separate state for edit panel
  const [isEditingFunction, setIsEditingFunction] = useState(false)
  const [isEditingProcedure, setIsEditingProcedure] = useState(false)
  const [isEditingTrigger, setIsEditingTrigger] = useState(false)
  
  // Context menu modals state
  const [showDiagramModal, setShowDiagramModal] = useState(false)
  
  // State for tracking recently analyzed tables with timestamps
  const [analyzedTables, setAnalyzedTables] = useState<Map<string, number>>(new Map())
  
  // Function to mark a table as analyzed and clean up old entries
  const markTableAsAnalyzed = useCallback((database: string, tableName: string, schema?: string) => {
    const tableKey = schema ? `${database}.${schema}.${tableName}` : `${database}.${tableName}`
    const now = Date.now()
    
    setAnalyzedTables(prev => {
      const newMap = new Map(prev)
      
      // Add the new analyzed table
      newMap.set(tableKey, now)
      
      // Clean up entries older than 5 seconds
      const cutoff = now - 5000
      for (const [key, timestamp] of newMap.entries()) {
        if (timestamp < cutoff) {
          newMap.delete(key)
        }
      }
      
      return newMap
    })
    
    // Auto-remove this table after 5 seconds
    setTimeout(() => {
      setAnalyzedTables(prev => {
        const newMap = new Map(prev)
        if (newMap.get(tableKey) === now) {
          newMap.delete(tableKey)
        }
        return newMap
      })
    }, 5000)
  }, [])
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false)
  const [placeholderModalData, setPlaceholderModalData] = useState<{
    title: string
    action: string
    data: unknown
  } | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportModalData, setExportModalData] = useState<{
    database: string
    table: string
    schema?: string
    columns: { name: string; type: string; dataType: string }[]
    totalRows: number
  } | null>(null)
  const [contextMenuTarget, setContextMenuTarget] = useState<{
    database: string
    schema?: string
    table?: string
    view?: string
    function?: string
    procedure?: string
    index?: string
    trigger?: string
    sequence?: string
    user?: string
    folderType?: string
  } | null>(null)

  // Callback functions
  const handleMultiDrop = useCallback((selectedItems: string[]) => {
    if (!connectionId) return
    
    // Parse selected items to extract their details
    const itemsToDelete = selectedItems.map(itemId => {
      const parts = itemId.split('-')
      const [type] = parts
      
      if (type === 'database') {
        const [, name] = parts
        return {
          type: 'database' as const,
          name,
          database: name,
          schema: undefined
        }
      } else if (type === 'schema') {
        const [, database, schema] = parts
        return {
          type: 'schema' as const,
          name: schema,
          database,
          schema
        }
      } else {
        // For other objects
        const [, database, rawSchema, ...nameParts] = parts
        const schema = rawSchema === 'default' ? undefined : rawSchema
        const name = nameParts.join('-') // In case name contains dashes
        
        return {
          type: type as 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event',
          name,
          database,
          schema
        }
      }
    }).filter(item => 
      // Only include valid droppable types
      ['database', 'schema', 'table', 'view', 'function', 'procedure', 'trigger', 'sequence', 'extension', 'index', 'event'].includes(item.type)
    )
    
    if (itemsToDelete.length === 0) return
    
    // Initiate bulk drop operation
    initiateBulkDropOperation(itemsToDelete, connectionId)
  }, [connectionId, initiateBulkDropOperation])

  const handleContextMenuAction = useCallback((action: string, data?: ContextMenuActionData) => {
    if (data) {
      setContextMenuTarget(data as typeof contextMenuTarget)
    } else {
      setContextMenuTarget(null)
    }
    
    const showPlaceholder = (title: string) => {
      setPlaceholderModalData({ title, action, data })
      setShowPlaceholderModal(true)
    }
    
    // Handle actions that don't require data first
    if (action === 'create-database') {
      setSelectedItem({
        type: 'create-database',
        connectionId: connectionId || '',
        name: 'create-database'
      })
      return
    }
    
    // Add null check for data
    if (!data) return
    
    // Comprehensive type guard functions for ContextMenuActionData
    
    const hasDatabaseAndTable = (data: ContextMenuActionData): data is { database: string; table: string; schema?: string } => 
      'database' in data && 'table' in data
    
    const hasDatabaseAndView = (data: ContextMenuActionData): data is { database: string; view: string; schema?: string } => 
      'database' in data && 'view' in data
    
    const hasDatabaseAndFunction = (data: ContextMenuActionData): data is { database: string; function: string; schema?: string } => 
      'database' in data && 'function' in data
    
    const hasDatabaseAndProcedure = (data: ContextMenuActionData): data is { database: string; procedure: string; schema?: string } => 
      'database' in data && 'procedure' in data
    
    const hasDatabaseAndIndex = (data: ContextMenuActionData): data is { database: string; index: string; schema?: string } => 
      'database' in data && 'index' in data
    
    const hasDatabaseAndTrigger = (data: ContextMenuActionData): data is { database: string; trigger: string; schema?: string } => 
      'database' in data && 'trigger' in data
    
    const hasDatabaseAndSequence = (data: ContextMenuActionData): data is { database: string; sequence: string; schema?: string } => 
      'database' in data && 'sequence' in data
    
    
    const hasDatabaseAndUser = (data: ContextMenuActionData): data is { database: string; user: string; schema?: string } => 
      'database' in data && 'user' in data
    
    const hasDatabaseAndSchema = (data: ContextMenuActionData): data is { database: string; schema: string } => 
      'database' in data && 'schema' in data
    
    const hasDatabaseAndFolderType = (data: ContextMenuActionData): data is { database: string; schema?: string; folderType: string } => 
      'database' in data && 'folderType' in data
    
    const hasSelectedItems = (data: ContextMenuActionData): data is { selectedItems: string[] } | { selectedItems: { name: string; type: string; database?: string; schema?: string }[] } => 
      'selectedItems' in data && Array.isArray(data.selectedItems)
    
    const hasTypeAndName = (data: ContextMenuActionData): data is { type: 'table' | 'view'; database: string; schema?: string; name: string } => 
      'type' in data && 'name' in data && 'database' in data && 
      typeof data.type === 'string' && typeof data.name === 'string' && typeof data.database === 'string'
    
    switch (action) {
      case 'select-query':
        // Navigate to query editor with pre-filled SELECT query in a new tab
        if (hasDatabaseAndTable(data)) {
          const tableName = data.schema ? `${data.schema}.${data.table}` : data.table
          const query = encodeURIComponent(`SELECT * FROM ${tableName} LIMIT 200;`)
          const database = encodeURIComponent(data.database)
          const schema = data.schema ? encodeURIComponent(data.schema) : ''
          
          const url = `/query/${connectionId}?query=${query}&database=${database}${schema ? `&schema=${schema}` : ''}&autoExecute=true&newTab=true`
          navigate(url)
        }
        break
      case 'insert-query':
        // Navigate to query editor with pre-filled INSERT query template in a new tab
        if (hasDatabaseAndTable(data) && connectionId) {
          // Fetch table columns to generate INSERT template
          const createInsertQuery = async () => {
            try {
              if (!hasDatabaseAndTable(data)) {
                console.error('Missing database or table name')
                return
              }
              
              console.log('Fetching column info for table:', data.table, 'in database:', data.database, 'schema:', data.schema)
              
              const response = await AgentProxyService.getColumnInfo(
                connectionId,
                data.database,
                data.table,
                data.schema
              )
              
              console.log('Column info response:', response)
              
              // Handle different response formats more robustly
              let columns: { 
                name?: string; 
                columnName?: string; 
                type?: string; 
                dataType?: string; 
                column_name?: string; 
                data_type?: string;
                character_maximum_length?: number;
                numeric_precision?: number;
                numeric_scale?: number;
              }[] = []
              
              // Recursive function to extract column data from nested structures
              const extractColumns = (obj: unknown): void => {
                if (Array.isArray(obj)) {
                  obj.forEach(item => extractColumns(item))
                } else if (obj && typeof obj === 'object') {
                  const objRecord = obj as Record<string, unknown>
                  
                  // Check if this object looks like a column definition
                  if (objRecord.column_name || objRecord.name || objRecord.columnName) {
                    columns.push({
                      name: (objRecord.name || objRecord.column_name || objRecord.columnName) as string,
                      columnName: (objRecord.columnName || objRecord.column_name || objRecord.name) as string,
                      column_name: objRecord.column_name as string,
                      type: (objRecord.type || objRecord.data_type || objRecord.dataType) as string,
                      dataType: (objRecord.dataType || objRecord.data_type || objRecord.type) as string,
                      data_type: objRecord.data_type as string,
                      character_maximum_length: typeof objRecord.character_maximum_length === 'number' ? objRecord.character_maximum_length : undefined,
                      numeric_precision: typeof objRecord.numeric_precision === 'number' ? objRecord.numeric_precision : undefined,
                      numeric_scale: typeof objRecord.numeric_scale === 'number' ? objRecord.numeric_scale : undefined
                    })
                  } else {
                    // Recursively check nested objects
                    Object.values(objRecord).forEach(value => extractColumns(value))
                  }
                }
              }
              
              extractColumns(response)
              
              console.log('Extracted columns:', columns)
              
              if (columns.length > 0 && hasDatabaseAndTable(data)) {
                const tableName = data.schema ? `${data.schema}.${data.table}` : data.table
                const columnNames = columns
                  .map(col => col.name || col.columnName || col.column_name)
                  .filter(Boolean)
                
                console.log('Column names for INSERT:', columnNames)
                console.log('Column details with constraints:', columns.map(col => ({
                  name: col.name || col.columnName || col.column_name,
                  type: col.type || col.dataType || col.data_type,
                  maxLength: col.character_maximum_length,
                  precision: col.numeric_precision,
                  scale: col.numeric_scale
                })))
                
                if (columnNames.length === 0) {
                  console.warn('No valid column names found, using fallback')
                  throw new Error('No valid column names extracted')
                }
                
                // Helper function to generate appropriate sample values based on column type and constraints
                const generateSampleValue = (col: typeof columns[0]): string => {
                  const colType = (col.type || col.dataType || col.data_type || '').toLowerCase()
                  const maxLength = col.character_maximum_length
                  
                  // Special debugging for potential CHAR(1) columns
                  const colName = (col.name || col.columnName || col.column_name || '').toLowerCase()
                  if (colName.includes('initial') || maxLength === 1 || colType.includes('character(1)') || colType === 'bpchar') {
                    console.error('🚨 POTENTIAL CHAR(1) COLUMN:', {
                      name: colName,
                      type: colType,
                      maxLength: maxLength,
                      rawColumn: col
                    })
                  }
                  
                  if (colType.includes('int') || colType.includes('number') || colType.includes('decimal') || colType.includes('float') || colType.includes('double') || colType.includes('numeric') || colType.includes('real') || colType.includes('serial') || colType.includes('bigserial') || colType.includes('smallint') || colType.includes('bigint') || colType.includes('money')) {
                    return '0'
                  } else if (colType.includes('bool')) {
                    return 'true'
                  } else if (colType.includes('date') || colType.includes('time')) {
                    return "'2024-01-01'"
                  } else if (colType.includes('json')) {
                    return '\'{"sample":"value"}\''
                  } else if (colType.includes('uuid')) {
                    return "'123e4567-e89b-12d3-a456-426614174000'"
                  } else if (colType.includes('array') || colType.includes('[]') || colType.endsWith('[]')) {
                    // Handle PostgreSQL arrays - they need {} syntax
                    if (colType.includes('text') || colType.includes('varchar') || colType.includes('char')) {
                      return "'{\"sample1\",\"sample2\"}'"
                    } else if (colType.includes('int') || colType.includes('number') || colType.includes('numeric')) {
                      return "'{1,2,3}'"
                    } else {
                      return "'{\"value1\",\"value2\"}'"
                    }
                  } else if (colType.includes('bytea')) {
                    return String.raw`'\x48656c6c6f'`  // "Hello" in hex
                  } else if (colType.includes('inet') || colType.includes('cidr')) {
                    return "'192.168.1.1'"
                  } else if (colType.includes('macaddr')) {
                    return "'08:00:2b:01:02:03'"
                  } else if (colType.includes('point')) {
                    return "'(1,1)'"
                  } else if (colType.includes('char') || colType.includes('text') || colType.includes('varchar') || colType.includes('character') || colType === 'bpchar') {
                    // Handle character/text types with length constraints
                    // This includes CHAR, VARCHAR, CHARACTER, TEXT, bpchar, etc.
                    let baseValue = 'sample'
                    
                    if (maxLength && maxLength > 0) {
                      // Handle length-constrained character types
                      if (maxLength === 1) {
                        // CHAR(1), VARCHAR(1), etc. - use single character
                        baseValue = 'a'
                      } else if (maxLength === 2) {
                        baseValue = 'ab'
                      } else if (maxLength <= 5) {
                        baseValue = 'test'
                      } else if (maxLength < 'sample'.length) {
                        // For any other small lengths, truncate to fit
                        baseValue = 'sample'.substring(0, maxLength)
                      } else {
                        // Length is sufficient for 'sample' or no constraint
                        baseValue = 'sample'
                      }
                    } else {
                      // Even without maxLength, if we detect certain patterns, be safe
                      if (colType === 'bpchar' || colType.includes('character(1)')) {
                        baseValue = 'a'
                      }
                    }

                    return `'${baseValue}'`
                  } else {
                    // Default fallback for unknown types
                    return "'sample'"
                  }
                }
                
                const templateValues = columns.map(generateSampleValue).slice(0, columnNames.length) // Ensure same length as column names
                
                // Find problematic columns that got 'sample' but might be numeric
                const problematicColumns = columns.slice(0, columnNames.length).filter((col, i) => {
                  const value = templateValues[i]
                  const colName = (col.name || col.columnName || col.column_name || '').toLowerCase()
                  const colType = (col.type || col.dataType || col.data_type || '').toLowerCase()
                  return value === "'sample'" && (
                    colName.includes('salary') || 
                    colName.includes('id') || 
                    colName.includes('price') || 
                    colName.includes('amount') || 
                    colName.includes('count') ||
                    colType.includes('numeric') ||
                    colType === 'money'
                  )
                })
                if (problematicColumns.length > 0) {
                  console.error('🚨 POTENTIAL NUMERIC COLUMNS DETECTED AS TEXT:', problematicColumns)
                }
                
                const query = encodeURIComponent(
                  `INSERT INTO ${tableName} (${columnNames.join(', ')})\nVALUES (${templateValues.join(', ')});`
                )
                const database = encodeURIComponent(data.database)
                const schema = data.schema ? encodeURIComponent(data.schema) : ''
                
                console.log('Generated INSERT query:', decodeURIComponent(query))

                const url = `/query/${connectionId}?query=${query}&database=${database}${schema ? `&schema=${schema}` : ''}&newTab=true`
                navigate(url)
                return
              }
              
              // Fallback if no columns found - should not happen with proper column fetching
              console.warn('No columns found, using generic INSERT template')
              throw new Error('No columns found in response')
              
            } catch (error) {
              console.error('Error fetching table columns for INSERT template:', error)
              // Enhanced fallback - still use generic template but with better messaging
              if (hasDatabaseAndTable(data)) {
                const tableName = data.schema ? `${data.schema}.${data.table}` : data.table
                const query = encodeURIComponent(`-- Could not fetch column information for ${tableName}\n-- Please replace col1, col2 with actual column names\n\nINSERT INTO ${tableName} (col1, col2)\nVALUES ('val1', 'val2');`)
                const database = encodeURIComponent(data.database)
                const schema = data.schema ? encodeURIComponent(data.schema) : ''
                
                const url = `/query/${connectionId}?query=${query}&database=${database}${schema ? `&schema=${schema}` : ''}&newTab=true`
                navigate(url)
              }
            }
          }
          
          void createInsertQuery()
        }
        break
      case 'create-table':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-table',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-table'
          })
        }
        break
      case 'create-schema':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-schema',
            connectionId: connectionId || '',
            database: data.database,
            name: 'create-schema'
          })
        }
        break
      case 'create-view':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-view',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-view'
          })
        }
        break
      case 'create-function':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-function',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-function'
          })
        }
        break
      case 'create-procedure':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-procedure',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-procedure'
          })
        }
        break
      case 'create-index':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-index',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-index',
            table: 'table' in data ? data.table : undefined // Pass table info when creating index from specific table
          })
        }
        break
      case 'create-trigger':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-trigger',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-trigger'
          })
        }
        break
      case 'create-event':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-event',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-event'
          })
        }
        break
      case 'create-extension':
        if ('database' in data) {
          setSelectedItem({
            type: 'create-extension',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: 'create-extension'
          })
        }
        break
      case 'view-diagram':
        setShowDiagramModal(true)
        break
      case 'edit-table':
        if (hasDatabaseAndTable(data)) {
          // Expand tree to show the selected table
          databaseTreeRef.current?.expandToNode(data.database, data.schema, 'tables')
          
          setSelectedItem({
            type: 'table',
            connectionId: connectionId || '',
            database: data.database,
            schema: data.schema,
            name: data.table,
            fullPath: data.schema ? `${data.database}.${data.schema}.${data.table}` : `${data.database}.${data.table}`
          })
          setIsEditingTable(true)
        }
        break
      case 'edit-view':
        if (hasDatabaseAndView(data)) {
          // Expand tree to show the selected view
          databaseTreeRef.current?.expandToNode(data.database, data.schema, 'views')
          
          setSelectedItem({
            type: 'view',
            connectionId: connectionId || '',
            database: data.database,
            schema: data.schema,
            name: data.view,
            fullPath: data.schema ? `${data.database}.${data.schema}.${data.view}` : `${data.database}.${data.view}`
          })
          setIsEditingView(true)  // Show ViewDetailsView
          setIsViewEditPanelOpen(true)  // Open the edit panel
        }
        break
      case 'edit-function':
        if (hasDatabaseAndFunction(data)) {
          // Expand tree to show the selected function
          databaseTreeRef.current?.expandToNode(data.database, data.schema, 'functions')
          
          setSelectedItem({
            type: 'function',
            connectionId: connectionId || '',
            database: data.database,
            schema: data.schema,
            name: data.function,
            arguments: 'arguments' in data ? data.arguments as string : undefined,  // Include arguments if available
            fullPath: data.schema ? `${data.database}.${data.schema}.${data.function}` : `${data.database}.${data.function}`
          })
          setIsEditingFunction(true)
        }
        break
      case 'edit-procedure':
        if (hasDatabaseAndProcedure(data)) {
          // Expand tree to show the selected procedure
          databaseTreeRef.current?.expandToNode(data.database, data.schema, 'procedures')
          
          setSelectedItem({
            type: 'procedure',
            connectionId: connectionId || '',
            database: data.database,
            schema: data.schema,
            name: data.procedure,
            fullPath: data.schema ? `${data.database}.${data.schema}.${data.procedure}` : `${data.database}.${data.procedure}`
          })
          setIsEditingProcedure(true)
        }
        break
      case 'edit-trigger':
        if (hasDatabaseAndTrigger(data)) {
          // Expand tree to show the selected trigger
          databaseTreeRef.current?.expandToNode(data.database, data.schema, 'triggers')
          
          setSelectedItem({
            type: 'trigger',
            connectionId: connectionId || '',
            database: data.database,
            schema: data.schema,
            name: data.trigger,
            fullPath: data.schema ? `${data.database}.${data.schema}.${data.trigger}` : `${data.database}.${data.trigger}`
          })
          setIsEditingTrigger(true)
        }
        break
      case 'edit-user':
        showPlaceholder('Edit User')
        break
      case 'duplicate-table':
        showPlaceholder('Duplicate Table')
        break
      case 'duplicate-view':
        if (hasDatabaseAndView(data) && connectionId && dataSource) {
          // Duplicate view with fetched definition - needs to be async
          const duplicateViewWithDefinition = async () => {
            try {
              // Fetch view definition to get all details
              const viewDetailsResponse = await AgentProxyService.getViewDefinition(
                connectionId,
                data.database,
                data.view,
                data.schema
              )
              
              // Generate a new view name
              const originalName = data.view
              const newName = `${originalName}_copy`
              
              // Navigate to create view with duplicated data
              setSelectedItem({
                type: 'create-view',
                connectionId: connectionId || '',
                database: data.database,
                schema: data.schema,
                name: 'create-view',
                // Store the duplication data for the create view to use
                duplicateFrom: {
                  originalName,
                  newName,
                  definition: viewDetailsResponse
                }
              })
            } catch (error) {
              console.error('Error duplicating view:', error)
              // Fallback: just open create view with suggested name
              setSelectedItem({
                type: 'create-view',
                connectionId: connectionId || '',
                database: data.database,
                schema: data.schema,
                name: 'create-view',
                duplicateFrom: {
                  originalName: data.view,
                  newName: `${data.view}_copy`,
                  definition: null
                }
              })
            }
          }
          
          // Execute the async view duplication
          void duplicateViewWithDefinition()
        }
        break
      case 'export-data':
        // Show export modal with table/view metadata
        if ((hasDatabaseAndTable(data) || hasDatabaseAndView(data)) && connectionId) {
          const tableName = 'table' in data ? data.table : 'view' in data ? data.view : null
          if (tableName) {
            // Fetch columns and metadata for the table/view
            const fetchExportData = async () => {
              try {
                // Get columns information
                const columnsResponse = await AgentProxyService.getColumnInfo(
                  connectionId,
                  data.database,
                  tableName,
                  data.schema
                )
                
                // Get row count by fetching first page with limit 1
                const countResponse = await AgentProxyService.getTableData(
                  connectionId,
                  data.database,
                  tableName,
                  { schemaName: data.schema, limit: 1, page: 1 }
                ) as { totalCount?: number }
                
                const columns = Array.isArray(columnsResponse) 
                  ? columnsResponse.map((col: unknown) => {
                      const colObj = col as Record<string, unknown>
                      return {
                        name: (colObj.column_name || colObj.name || '') as string,
                        type: (colObj.data_type || colObj.type || 'TEXT') as string,
                        dataType: (colObj.data_type || colObj.type || 'TEXT') as string
                      }
                    })
                  : []
                
                const totalRows = countResponse?.totalCount || 0
                
                setExportModalData({
                  database: data.database,
                  table: tableName,
                  schema: data.schema,
                  columns,
                  totalRows
                })
                setShowExportModal(true)
              } catch (error) {
                console.error('Failed to fetch export data:', error)
                showToast('error', 'Failed to load export data')
              }
            }
            
            void fetchExportData()
          }
        }
        break
      case 'drop-database':
        if (connectionId && 'database' in data) {
          // Immediately clear selected item if it's the database being dropped
          if (selectedItem?.type === 'database' && (selectedItem.database === data.database || selectedItem.name === data.database)) {
            setSelectedItem(null)
          }
          // Also clear if the selected item belongs to the database being dropped
          else if (selectedItem?.database === data.database) {
            setSelectedItem(null)
          }
          
          // Clear database expansion state immediately to prevent further queries
          if (databaseTreeRef.current) {
            databaseTreeRef.current.clearDatabaseExpansion(data.database)
          }
          
          initiateDropOperation({
            type: 'database',
            name: data.database,
            database: data.database,
            connectionId
          })
        }
        break
      case 'drop-schema':
        if (connectionId && hasDatabaseAndSchema(data)) {
          initiateDropOperation({
            type: 'schema',
            name: data.schema,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-table':
        if (connectionId && hasDatabaseAndTable(data)) {
          initiateDropOperation({
            type: 'table',
            name: data.table,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-view':
        if (connectionId && hasDatabaseAndView(data)) {
          initiateDropOperation({
            type: 'view',
            name: data.view,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-function':
        if (connectionId && hasDatabaseAndFunction(data)) {
          initiateDropOperation({
            type: 'function',
            name: data.function,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-procedure':
        if (connectionId && hasDatabaseAndProcedure(data)) {
          initiateDropOperation({
            type: 'procedure',
            name: data.procedure,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-index':
        if (connectionId && hasDatabaseAndIndex(data)) {
          initiateDropOperation({
            type: 'index',
            name: data.index,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-trigger':
        if (connectionId && hasDatabaseAndTrigger(data)) {
          initiateDropOperation({
            type: 'trigger',
            name: data.trigger,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-sequence':
        if (connectionId && hasDatabaseAndSequence(data)) {
          initiateDropOperation({
            type: 'sequence',
            name: data.sequence,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-user':
        if (connectionId && hasDatabaseAndUser(data)) {
          initiateDropOperation({
            type: 'user',
            name: data.user,
            database: data.database,
            schema: data.schema,
            connectionId
          })
        }
        break
      case 'drop-multiple':
        if (connectionId && hasSelectedItems(data)) {
          const {selectedItems} = data
          if (Array.isArray(selectedItems) && selectedItems.length > 0) {
            // Check if any of the items being dropped are databases that match the currently selected item
            let shouldClearSelection = false
            
            if (selectedItem) {
              if (typeof selectedItems[0] === 'string') {
                // String format: check if any item starts with 'database-' and matches
                shouldClearSelection = (selectedItems as string[]).some(itemId => {
                  const parts = itemId.split('-')
                  const [type, name] = parts
                  if (type === 'database') {
                    return selectedItem.type === 'database' && (selectedItem.database === name || selectedItem.name === name) ||
                           selectedItem.database === name
                  }
                  return false
                })
              } else {
                // Object format: check if any database being dropped matches
                shouldClearSelection = (selectedItems as { name: string; type: string; database?: string; schema?: string }[]).some(item => {
                  if (item.type === 'database') {
                    return selectedItem.type === 'database' && (selectedItem.database === item.name || selectedItem.name === item.name) ||
                           selectedItem.database === item.name
                  }
                  return false
                })
              }
            }
            
            if (shouldClearSelection) {
              setSelectedItem(null)
            }
            
            // Clear expansion state for databases being dropped
            if (databaseTreeRef.current) {
              if (typeof selectedItems[0] === 'string') {
                selectedItems.forEach(itemId => {
                  const parts = (itemId as string).split('-')
                  const [type, name] = parts
                  if (type === 'database') {
                    databaseTreeRef.current!.clearDatabaseExpansion(name)
                  }
                })
              } else {
                (selectedItems as { name: string; type: string; database?: string; schema?: string }[]).forEach(item => {
                  if (item.type === 'database') {
                    databaseTreeRef.current!.clearDatabaseExpansion(item.name)
                  }
                })
              }
            }
            
            if (typeof selectedItems[0] === 'string') {
              handleMultiDrop(selectedItems as string[])
            } else {
              handleMultiDrop((selectedItems as { name: string; type: string; database?: string; schema?: string }[]).map((item) => item.name || '').filter(Boolean))
            }
          }
        }
        break
      case 'export-multiple':
        if (hasSelectedItems(data)) {
          setPlaceholderModalData({ 
            title: `Export ${data.selectedItems.length} Items`, 
            action: 'export-multiple', 
            data: { items: data.selectedItems }
          })
          setShowPlaceholderModal(true)
        }
        break
      case 'execute-function':
        if (hasDatabaseAndFunction(data) && connectionId && dataSource) {
          // Execute function with fetched arguments - needs to be async
          const executeFunctionWithArgs = async () => {
            try {
              // Create connection object for navigation
              const connection: DatabaseConnection = {
                id: dataSource.id!,
                name: dataSource.displayName!,
                type: dataSource.databaseType === 'MySQL' ? 'mysql' : 'postgresql',
                host: dataSource.host!,
                port: dataSource.port!,
                username: dataSource.username!,
                password: '',
                database: dataSource.defaultDatabase || undefined,
                createdAt: dataSource.createdAt!,
                updatedAt: dataSource.updatedAt!
              }
              
              // Try to get arguments from context menu data first, then fetch if needed
              let argumentsString: string | undefined = 'arguments' in data ? data.arguments as string : undefined
              
              // If no arguments in context menu data, fetch function details
              if (!argumentsString) {
                try {
                  const functionDetailsResponse = await AgentProxyService.getFunctionDefinition(
                    connectionId,
                    data.database,
                    data.function,
                    data.schema,
                    undefined // functionArguments
                  )
                  
                  // Extract arguments from function details response (similar to FunctionDetailsView)
                  const functionDetails = functionDetailsResponse
                  if (functionDetails && typeof functionDetails === 'object' && 
                      'metadata' in functionDetails && functionDetails.metadata &&
                      typeof functionDetails.metadata === 'object' &&
                      'parameters' in functionDetails.metadata &&
                      Array.isArray(functionDetails.metadata.parameters)) {
                    argumentsString = functionDetails.metadata.parameters
                      .map((param: unknown) => {
                        const paramObj = param as Record<string, unknown>
                        const mode = (paramObj.column_type as string) || 'IN'
                        const name = (paramObj.column_name as string) || 'param'
                        const type = (paramObj.full_data_type as string) || (paramObj.data_type as string) || 'unknown'
                        return `${mode} ${name} ${type}`
                      })
                      .join(', ')
                  }
                } catch (detailsError) {
                  console.warn('Could not fetch function details for arguments:', detailsError)
                  // Continue with undefined arguments - will generate basic template
                }
              }
              
              // Generate the function call query
              const functionCall = generateFunctionCall(
                data.function,
                argumentsString,
                connection.type,
                data.schema,
                data.database
              )
              
              // Navigate to query editor with the pre-filled query
              navigateToQueryEditor(connection, functionCall, data.database, data.schema, true)
            } catch (error) {
              console.error('Error generating function call:', error)
            }
          }
          
          // Execute the async function
          void executeFunctionWithArgs()
        }
        break
      case 'execute-procedure':
        if (hasDatabaseAndProcedure(data) && connectionId && dataSource) {
          // Execute procedure with fetched arguments - needs to be async
          const executeProcedureWithArgs = async () => {
            try {
              // Create connection object for navigation
              const connection: DatabaseConnection = {
                id: dataSource.id!,
                name: dataSource.displayName!,
                type: dataSource.databaseType === 'MySQL' ? 'mysql' : 'postgresql',
                host: dataSource.host!,
                port: dataSource.port!,
                username: dataSource.username!,
                password: '',
                database: dataSource.defaultDatabase || undefined,
                createdAt: dataSource.createdAt!,
                updatedAt: dataSource.updatedAt!
              }
              
              // Fetch procedure details to get the arguments
              let argumentsString: string | undefined = undefined
              try {
                const procedureDetailsResponse = await AgentProxyService.getProcedureDefinition(
                  connectionId,
                  data.database,
                  data.procedure,
                  data.schema
                )
                
                // Extract arguments from procedure details response (similar to ProcedureDetailsView)
                const procedureDetails = procedureDetailsResponse
                if (procedureDetails && typeof procedureDetails === 'object' && 
                    'metadata' in procedureDetails && procedureDetails.metadata &&
                    typeof procedureDetails.metadata === 'object' &&
                    'parameters' in procedureDetails.metadata &&
                    Array.isArray(procedureDetails.metadata.parameters)) {
                  argumentsString = procedureDetails.metadata.parameters
                    .map((param: unknown) => {
                      const paramObj = param as Record<string, unknown>
                      const mode = (paramObj.column_type as string) || 'IN'
                      const name = (paramObj.column_name as string) || 'param'
                      const type = (paramObj.full_data_type as string) || (paramObj.data_type as string) || 'unknown'
                      return `${mode} ${name} ${type}`
                    })
                    .join(', ')
                }
              } catch (detailsError) {
                console.warn('Could not fetch procedure details for arguments:', detailsError)
                // Continue with undefined arguments - will generate basic template
              }
              
              // Generate the procedure call with fetched arguments
              const procedureCall = generateProcedureCall(
                data.procedure,
                argumentsString,
                connection.type,
                data.schema,
                data.database
              )
              
              // Navigate to query editor with the pre-filled query
              navigateToQueryEditor(connection, procedureCall, data.database, data.schema, true)
            } catch (error) {
              console.error('Error generating procedure call:', error)
            }
          }
          
          // Execute the async procedure
          void executeProcedureWithArgs()
        }
        break
      case 'duplicate-function':
        if (hasDatabaseAndFunction(data) && connectionId && dataSource) {
          // Duplicate function with fetched definition - needs to be async
          const duplicateFunctionWithDefinition = async () => {
            try {
              // Fetch function definition to get all details
              const functionDetailsResponse = await AgentProxyService.getFunctionDefinition(
                connectionId,
                data.database,
                data.function,
                data.schema,
                'arguments' in data ? data.arguments as string : undefined
              )
              
              // Generate a new function name
              const originalName = data.function
              const newName = `${originalName}_copy`
              
              // Navigate to create function view with duplicated data
              setSelectedItem({
                type: 'create-function',
                connectionId: connectionId || '',
                database: data.database,
                schema: data.schema,
                name: 'create-function',
                // Store the duplication data for the create view to use
                duplicateFrom: {
                  originalName,
                  newName,
                  definition: functionDetailsResponse
                }
              })
            } catch (error) {
              console.error('Error duplicating function:', error)
              // Fallback: just open create function view with suggested name
              setSelectedItem({
                type: 'create-function',
                connectionId: connectionId || '',
                database: data.database,
                schema: data.schema,
                name: 'create-function',
                duplicateFrom: {
                  originalName: data.function,
                  newName: `${data.function}_copy`,
                  definition: null
                }
              })
            }
          }
          
          void duplicateFunctionWithDefinition()
        }
        break
      case 'duplicate-procedure':
        if (hasDatabaseAndProcedure(data) && connectionId && dataSource) {
          // Duplicate procedure with fetched definition - needs to be async
          const duplicateProcedureWithDefinition = async () => {
            try {
              // Fetch procedure definition to get all details
              const procedureDetailsResponse = await AgentProxyService.getProcedureDefinition(
                connectionId,
                data.database,
                data.procedure,
                data.schema
              )
              
              // Generate a new procedure name
              const originalName = data.procedure
              const newName = `${originalName}_copy`
              
              // Navigate to create procedure view with duplicated data
              setSelectedItem({
                type: 'create-procedure',
                connectionId: connectionId || '',
                database: data.database,
                schema: data.schema,
                name: 'create-procedure',
                // Store the duplication data for the create view to use
                duplicateFrom: {
                  originalName,
                  newName,
                  definition: procedureDetailsResponse
                }
              })
            } catch (error) {
              console.error('Error duplicating procedure:', error)
              // Fallback: just open create procedure view with suggested name
              setSelectedItem({
                type: 'create-procedure',
                connectionId: connectionId || '',
                database: data.database,
                schema: data.schema,
                name: 'create-procedure',
                duplicateFrom: {
                  originalName: data.procedure,
                  newName: `${data.procedure}_copy`,
                  definition: null
                }
              })
            }
          }
          
          void duplicateProcedureWithDefinition()
        }
        break
      case 'view-index':
        showPlaceholder('View Index Definition')
        break
      case 'extension-info':
        if ('extension' in data && 'database' in data && data.extension && data.database) {
          setSelectedItem({
            type: 'extension',
            connectionId: connectionId || '',
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            name: data.extension,
            fullPath: ('schema' in data && data.schema) ? `${data.database}.${data.schema}.${data.extension}` : `${data.database}.${data.extension}`
          })
        }
        break
      case 'drop-extension':
        if (connectionId && 'database' in data && 'extension' in data && data.database && data.extension) {
          initiateDropOperation({
            type: 'extension',
            name: data.extension,
            database: data.database,
            schema: 'schema' in data ? data.schema : undefined,
            connectionId
          })
        }
        break
      case 'refresh':
        // Smart refresh - only refresh the specific node and its visible children
        if (databaseTreeRef.current) {
          console.log('Refresh action data:', data)
          
          // Determine the type based on the data structure
          let type: string | undefined = hasTypeAndName(data) ? data.type : undefined
          if (!type) {
            if (hasDatabaseAndFolderType(data)) {
              type = 'folder'
            } else if (hasDatabaseAndSchema(data)) {
              type = 'schema'
            } else if ('database' in data && !('schema' in data)) {
              type = 'database'
            } else {
              type = 'databases-root'
            }
          }
          
          const refreshData: RefreshData = {
            type,
            database: 'database' in data ? data.database : undefined,
            schema: 'schema' in data ? data.schema : undefined,
            folderType: hasDatabaseAndFolderType(data) ? data.folderType : undefined
          }
          
          console.log('Calling refresh with:', refreshData)
          databaseTreeRef.current.refresh(refreshData)
        }
        break
      case 'analyze-table':
        if (hasDatabaseAndTable(data) && connectionId) {
          // Execute ANALYZE command for single table
          const executeAnalyze = async () => {
            try {
              const tableName = data.schema ? `${data.schema}.${data.table}` : data.table
              // Use correct ANALYZE syntax based on database type
              const analyzeQuery = dataSource?.databaseType === 'MySQL' 
                ? `ANALYZE TABLE ${tableName};` 
                : `ANALYZE ${tableName};`
              
              const result = await AgentProxyService.executeQuery(
                connectionId,
                analyzeQuery,
                data.database
              )
              
              console.log('Analyze result:', result)
              
              // Show success message
              if (result) {
                console.log(`Successfully analyzed table: ${tableName}`)
                
                // Mark table as analyzed to show visual indicator
                markTableAsAnalyzed(data.database, data.table, data.schema)
                
                // Refresh tables and views folders to show updated row counts
                if (databaseTreeRef.current) {
                  // Refresh tables folder
                  databaseTreeRef.current.refresh({
                    type: 'folder',
                    database: data.database,
                    schema: data.schema,
                    folderType: 'tables-folder'
                  })
                  // Refresh views folder as well since the analyzed item could be a view
                  databaseTreeRef.current.refresh({
                    type: 'folder',
                    database: data.database,
                    schema: data.schema,
                    folderType: 'views-folder'
                  })
                }
              }
            } catch (error) {
              console.error('Failed to analyze table:', error)
              // You could add error handling/notification here
            }
          }
          
          executeAnalyze()
        }
        break
      case 'analyze-multiple':
        if (hasSelectedItems(data) && connectionId) {
          // Execute ANALYZE commands concurrently for multiple tables and views
          const executeMultipleAnalyze = async () => {
            try {
              // Filter only analyzable items (tables and views) from selected items
              const analyzableItems = Array.isArray(data.selectedItems) && data.selectedItems.length > 0 && typeof data.selectedItems[0] === 'string'
                ? (data.selectedItems as string[]).filter(itemId => itemId.startsWith('table-') || itemId.startsWith('view-'))
                : (data.selectedItems as { name: string; type: string; database?: string; schema?: string }[]).filter(item => 
                    item.type === 'table' || item.type === 'view'
                  )
              
              if (analyzableItems.length === 0) {
                console.warn('No analyzable items selected for analysis')
                return
              }
              
              // Create analyze promises for concurrent execution
              const analyzePromises = analyzableItems.map(async (item) => {
                try {
                  let tableName: string
                  let database: string
                  let schema: string | undefined
                  
                  if (typeof item === 'string') {
                    // Parse string format: "table-database-schema-tablename" or "table-database-tablename"
                    const parts = item.split('-')
                    const [, db, sch] = parts
                    database = db
                    if (parts.length > 3) {
                      schema = sch
                      tableName = parts.slice(3).join('-')
                    } else {
                      tableName = parts.slice(2).join('-')
                    }
                  } else {
                    // Object format
                    const { name, database: db = '', schema: sch } = item
                    tableName = name
                    database = db
                    schema = sch
                  }
                  
                  const fullTableName = schema ? `${schema}.${tableName}` : tableName
                  // Use correct ANALYZE syntax based on database type
                  const analyzeQuery = dataSource?.databaseType === 'MySQL' 
                    ? `ANALYZE TABLE ${fullTableName};` 
                    : `ANALYZE ${fullTableName};`
                  
                  const result = await AgentProxyService.executeQuery(
                    connectionId,
                    analyzeQuery,
                    database
                  )
                  
                  // Mark table as analyzed for visual indicator
                  markTableAsAnalyzed(database, tableName, schema)
                  
                  return { table: fullTableName, success: true, result }
                } catch (error) {
                  console.error(`Failed to analyze table:`, error)
                  return { table: typeof item === 'string' ? item : item.name, success: false, error }
                }
              })
              
              // Execute all analyze commands concurrently
              const results = await Promise.all(analyzePromises)
              
              // Log results
              const successful = results.filter(r => r.success).length
              const failed = results.filter(r => !r.success).length
              
              console.log(`Analyze complete: ${successful} successful, ${failed} failed`)
              
              // You could add a toast notification here with the results
              if (failed === 0) {
                console.log(`Successfully analyzed ${successful} tables`)
              } else {
                console.log(`Analyzed ${successful} tables, ${failed} failed`)
              }
              
              // Refresh only the tables and views folders to show updated row counts
              if (successful > 0 && databaseTreeRef.current) {
                // Get unique database/schema combinations to refresh affected folders
                const schemasToRefresh = new Set<string>()
                
                analyzableItems.forEach(item => {
                  let database: string
                  let schema: string | undefined
                  
                  if (typeof item === 'string') {
                    const parts = item.split('-')
                    const [, db, sch] = parts
                    database = db
                    if (parts.length > 3) {
                      schema = sch
                    }
                  } else {
                    const { database: db = '', schema: sch } = item
                    database = db
                    schema = sch
                  }
                  
                  const key = schema ? `${database}.${schema}` : database
                  schemasToRefresh.add(key)
                })
                
                // Refresh each unique schema's tables and views folders
                schemasToRefresh.forEach(key => {
                  const [database, schema] = key.split('.')
                  if (databaseTreeRef.current) {
                    // Refresh tables folder
                    databaseTreeRef.current.refresh({
                      type: 'folder',
                      database,
                      schema: schema === database ? undefined : schema,
                      folderType: 'tables-folder'
                    })
                    // Refresh views folder as well since views can be analyzed
                    databaseTreeRef.current.refresh({
                      type: 'folder',
                      database,
                      schema: schema === database ? undefined : schema,
                      folderType: 'views-folder'
                    })
                  }
                })
              }
            } catch (error) {
              console.error('Failed to execute multiple analyze:', error)
            }
          }
          
          executeMultipleAnalyze()
        }
        break
      default:
        console.log('Unhandled context menu action:', action, data)
    }
  }, [connectionId, navigate, setSelectedItem, setShowDiagramModal, setPlaceholderModalData, setShowPlaceholderModal, setIsEditingTable, setIsEditingView, setIsViewEditPanelOpen, setIsEditingFunction, setIsEditingProcedure, setIsEditingTrigger, initiateDropOperation, handleMultiDrop, databaseTreeRef, contextMenuTarget, selectedItem, markTableAsAnalyzed, dataSource, showToast])

  const handleItemSelect = useCallback((item: {
    type: 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event' | 'indexes-folder' | 'tables-folder' | 'views-folder' | 'functions-folder' | 'procedures-folder' | 'triggers-folder' | 'sequences-folder' | 'extensions-folder' | 'events-folder' | 'schemas-folder' | 'create-database' | 'create-table'
    database: string
    name: string
    schema?: string
  }) => {
    setSelectedItem({
      ...item,
      connectionId: connectionId || '',
      fullPath: item.schema ? `${item.database}.${item.schema}.${item.name}` : `${item.database}.${item.name}`
    })
    // Reset edit mode when selecting a different item
    setIsEditingTable(false)
    // For views, default to showing data (not properties)
    setIsEditingView(false)  // Show data by default
    setIsViewEditPanelOpen(false)  // Close any open edit panel
  }, [connectionId, setSelectedItem, setIsEditingTable, setIsEditingView])

  // Create callback functions for props
  const onDatabaseCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onTableCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onIndexCreated = useCallback(() => {
    setSelectedItem(null)
    
    // Invalidate indexes query to refresh the list
    if (selectedItem && connectionId) {
      void queryClient.invalidateQueries({
        queryKey: [...databaseKeys.all, 'indexes', connectionId, selectedItem.database!, selectedItem.schema]
      })
    }
  }, [setSelectedItem, selectedItem, connectionId, queryClient])

  const onIndexDeleted = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onTriggerCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onFunctionCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onSequenceCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onEventCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onProcedureCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onSchemaCreated = useCallback(() => {
    setSelectedItem(null)
  }, [setSelectedItem])

  const onViewCreated = useCallback(() => {
    // Navigate to views folder
    if (selectedItem) {
      setSelectedItem({
        type: 'views-folder',
        connectionId: selectedItem.connectionId,
        database: selectedItem.database!,
        schema: selectedItem.schema,
        name: 'Views',
        fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.Views` : `${selectedItem.database}.Views`
      })
      // Invalidate views query to refresh the list
      void queryClient.invalidateQueries({
        queryKey: [...databaseKeys.all, 'views', selectedItem.connectionId, selectedItem.database!, selectedItem.schema]
      })
    }
  }, [selectedItem, setSelectedItem, queryClient])

  const onExtensionInstalled = useCallback(() => {
    // Refresh the extensions list
    if (selectedItem && connectionId) {
      void queryClient.invalidateQueries({ 
        queryKey: [...databaseKeys.all, 'extensions', connectionId, selectedItem.database] 
      })
    }
    setSelectedItem(null)
  }, [selectedItem, connectionId, setSelectedItem, queryClient])

  const onCloseTableEditor = useCallback(() => setIsEditingTable(false), [setIsEditingTable])
  const onEditStructure = useCallback(() => setIsEditingTable(true), [setIsEditingTable])
  const onEditPanelCloseView = useCallback(() => {
    setIsViewEditPanelOpen(false)  // Close the edit panel
    // Don't change isEditingView - stay on ViewDetailsView
  }, [])
  const onEditPanelCloseFunction = useCallback(() => setIsEditingFunction(false), [setIsEditingFunction])
  const onEditPanelCloseProcedure = useCallback(() => setIsEditingProcedure(false), [setIsEditingProcedure])
  const onEditPanelCloseTrigger = useCallback(() => setIsEditingTrigger(false), [setIsEditingTrigger])

  const onFunctionSelect = useCallback((functionName: string, functionArguments?: string) => {
    if (selectedItem && connectionId) {
      // Expand tree to show the selected function
      databaseTreeRef.current?.expandToNode(selectedItem.database!, selectedItem.schema, 'functions')
      
      setSelectedItem({
        type: 'function',
        connectionId: connectionId || '',
        database: selectedItem.database!,
        schema: selectedItem.schema,
        name: functionName,
        arguments: functionArguments,  // Include arguments for proper selection matching
        fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.${functionName}` : `${selectedItem.database}.${functionName}`
      })
    }
  }, [selectedItem, connectionId, setSelectedItem])

  const onProcedureSelect = useCallback((procedureName: string) => {
    if (selectedItem && connectionId) {
      // Expand tree to show the selected procedure
      databaseTreeRef.current?.expandToNode(selectedItem.database!, selectedItem.schema, 'procedures')
      
      setSelectedItem({
        type: 'procedure',
        connectionId: connectionId || '',
        database: selectedItem.database!,
        schema: selectedItem.schema,
        name: procedureName,
        fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.${procedureName}` : `${selectedItem.database}.${procedureName}`
      })
    }
  }, [selectedItem, connectionId, setSelectedItem])

  const onExtensionSelect = useCallback((extensionName: string) => {
    if (selectedItem && connectionId) {
      setSelectedItem({
        type: 'extension',
        connectionId: connectionId || '',
        database: selectedItem.database!,
        name: extensionName,
        schema: selectedItem.schema
      })
    }
  }, [selectedItem, connectionId, setSelectedItem])

  const onCloseDiagramModal = useCallback(() => setShowDiagramModal(false), [setShowDiagramModal])
  const onClosePlaceholderModal = useCallback(() => setShowPlaceholderModal(false), [setShowPlaceholderModal])
  const onCloseExportModal = useCallback(() => setShowExportModal(false), [setShowExportModal])

  // Helper functions for export
  const getContentTypeForFormat = useCallback((format: string): string => {
    switch (format) {
      case 'csv': return 'text/csv'
      case 'json': return 'application/json'
      case 'sql': return 'text/sql'
      default: return 'application/octet-stream'
    }
  }, [])

  const getExtensionForFormat = useCallback((format: string): string => {
    switch (format) {
      case 'csv': return 'csv'
      case 'json': return 'json'
      case 'sql': return 'sql'
      default: return 'txt'
    }
  }, [])

  const onExportData = useCallback(async (options: ExportOptions) => {
    if (!exportModalData || !connectionId) return
    
    try {
      // Fetch the actual table data (since backend export_data is incomplete)
      const limit = options.limit || exportModalData.totalRows
      const tableDataResponse = await AgentProxyService.getTableData(
        connectionId,
        exportModalData.database,
        exportModalData.table,
        {
          schemaName: exportModalData.schema,
          limit: limit,
          page: 1
        }
      ) as { rows?: unknown[][]; columns?: string[] }
      
      if (!tableDataResponse.rows || !tableDataResponse.columns) {
        throw new Error('No data available to export')
      }
      
      // Filter columns based on user selection
      const allColumns = tableDataResponse.columns
      const selectedColumnIndices = options.columns
        .map(colName => allColumns.indexOf(colName))
        .filter(idx => idx !== -1)
      
      const filteredColumns = selectedColumnIndices.map(idx => allColumns[idx])
      const filteredRows = tableDataResponse.rows.map(row => 
        selectedColumnIndices.map(idx => row[idx])
      )
      
      // Generate export content based on format
      let exportData: string
      
      switch (options.format) {
        case 'csv': {
          const csvRows = []
          if (options.includeHeaders) {
            csvRows.push(filteredColumns.join(','))
          }
          csvRows.push(...filteredRows.map(row => 
            row.map(cell => {
              const cellStr = cell === null || cell === undefined ? '' : 
                             typeof cell === 'string' || typeof cell === 'number' || typeof cell === 'boolean' ? String(cell) :
                             JSON.stringify(cell)
              // Escape CSV special characters
              return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') 
                ? `"${cellStr.replace(/"/g, '""')}"` 
                : cellStr
            }).join(',')
          ))
          exportData = csvRows.join('\n')
          break
        }
          
        case 'json': {
          const jsonData = filteredRows.map(row => {
            const obj: Record<string, unknown> = {}
            filteredColumns.forEach((col, idx) => {
              obj[col] = row[idx]
            })
            return obj
          })
          exportData = JSON.stringify(jsonData, null, 2)
          break
        }
          
        case 'sql': {
          const sqlRows = []
          if (options.includeHeaders) {
            sqlRows.push(`-- Exported data from ${exportModalData.table}`)
            sqlRows.push(`-- Columns: ${filteredColumns.join(', ')}`)
            sqlRows.push('')
          }
          filteredRows.forEach(row => {
            const values = row.map(cell => {
              if (cell === null || cell === undefined) return 'NULL'
              if (typeof cell === 'string') return `'${cell.replace(/'/g, "''")}'`
              if (typeof cell === 'number' || typeof cell === 'boolean') return String(cell)
              return JSON.stringify(cell)
            }).join(', ')
            sqlRows.push(`INSERT INTO ${exportModalData.table} (${filteredColumns.join(', ')}) VALUES (${values});`)
          })
          exportData = sqlRows.join('\n')
          break
        }
          
        default: {
          const neverFormat: never = options.format
          throw new Error(`Unsupported export format: ${String(neverFormat)}`)
        }
      }
      
      // Create and download the file
      const filename = `${exportModalData.table}.${getExtensionForFormat(options.format)}`
      const blob = new Blob([exportData], { type: getContentTypeForFormat(options.format) })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      showToast('success', `Data exported successfully as ${options.format.toUpperCase()}`)
    } catch (error) {
      console.error('Export failed:', error)
      showToast('error', 'Failed to export data')
    }
  }, [exportModalData, connectionId, showToast, getExtensionForFormat, getContentTypeForFormat])

  const onSetViewModeTableSizes = useCallback(() => setViewMode('table-sizes'), [setViewMode])
  const onSetViewModeExplorer = useCallback(() => setViewMode('explorer'), [setViewMode])

  // Memoize the selected item object for DatabaseTree
  const databaseTreeSelectedItem = useMemo(() => {
    if (selectedItem && selectedItem.database) {
      return {
        type: selectedItem.type,
        database: selectedItem.database,
        name: selectedItem.name,
        schema: selectedItem.schema,
        arguments: selectedItem.arguments
      }
    }
    return null
  }, [selectedItem])

  // Convert DataSource to DatabaseConnection format for compatibility
  const connectionForTable: DatabaseConnection = useMemo(() => {
    if (!dataSource) {
      return {
        id: '',
        name: '',
        type: 'mysql',
        host: '',
        port: 3306,
        username: '',
        password: '',
        database: undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    }
    
    return {
      id: dataSource.id!,
      name: dataSource.displayName!,
      type: dataSource.databaseType === 'MySQL' ? 'mysql' : 'postgresql',
      host: dataSource.host!,
      port: dataSource.port!,
      username: dataSource.username!,
      password: '', // Not available in DataSource
      database: dataSource.defaultDatabase || undefined,
      createdAt: dataSource.createdAt!,
      updatedAt: dataSource.updatedAt!
    }
  }, [dataSource])

  if (isLoadingConnection) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!dataSource || !connectionId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Connection Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            The requested database connection could not be found.
          </p>
        </div>
      </div>
    )
  }


  const renderContentView = () => {
    // Show table sizes view for database or schema selection
    if (viewMode === 'table-sizes' && selectedItem && 
        (selectedItem.type === 'database' || selectedItem.type === 'schema')) {
      return (
        <TableSizeView
          connectionId={connectionId}
          database={selectedItem.database || selectedItem.name}
          schema={selectedItem.type === 'schema' ? selectedItem.name : selectedItem.schema}
          dataSource={dataSource}
        />
      )
    }

    if (!selectedItem) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-6xl mb-4">🗄️</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Welcome to Database Explorer
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Select a database object from the tree to view its details
            </p>
          </div>
        </div>
      )
    }

    // Render database details when individual database is selected
    if (selectedItem.type === 'database') {
      return (
        <DatabaseDetailsView 
          connection={connectionForTable}
          database={selectedItem.database || selectedItem.name}
        />
      )
    }

    // Render create database view
    if (selectedItem.type === 'create-database' && dataSource.databaseType) {
      return (
        <CreateDatabaseView 
          connectionId={connectionId}
          databaseType={dataSource.databaseType}
          onDatabaseCreated={onDatabaseCreated}
          onCancel={() => setSelectedItem(null)}
        />
      )
    }

    // Render create table view
    if (selectedItem.type === 'create-table') {
      return (
        <CreateTableView 
          connectionId={connectionId}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onTableCreated={onTableCreated}
          onCancel={() => setSelectedItem(null)}
        />
      )
    }

    // Render create index view
    if (selectedItem.type === 'create-index') {
      return (
        <CreateIndexView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          table={selectedItem.table} // Pass pre-selected table
          onIndexCreated={onIndexCreated}
        />
      )
    }

    // Render create trigger view
    if (selectedItem.type === 'create-trigger') {
      return (
        <CreateTriggerView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onTriggerCreated={onTriggerCreated}
        />
      )
    }

    // Render create function view
    if (selectedItem.type === 'create-function') {
      return (
        <CreateFunctionView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onFunctionCreated={onFunctionCreated}
          duplicateFrom={selectedItem.duplicateFrom}
        />
      )
    }

    // Render create sequence view
    if (selectedItem.type === 'create-sequence') {
      return (
        <CreateSequenceView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onSequenceCreated={onSequenceCreated}
        />
      )
    }


    // Render create view view
    if (selectedItem.type === 'create-view') {
      return (
        <CreateViewView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onViewCreated={onViewCreated}
          duplicateFrom={selectedItem.duplicateFrom}
        />
      )
    }

    // Render create event view
    if (selectedItem.type === 'create-event') {
      return (
        <CreateEventView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onEventCreated={onEventCreated}
        />
      )
    }

    // Render create extension view
    if (selectedItem.type === 'create-extension') {
      return (
        <CreateExtensionView 
          connection={connectionForTable}
          database={selectedItem.database!}
          onExtensionInstalled={onExtensionInstalled}
        />
      )
    }

    // Render create procedure view
    if (selectedItem.type === 'create-procedure') {
      return (
        <CreateProcedureView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onProcedureCreated={onProcedureCreated}
          duplicateFrom={selectedItem.duplicateFrom}
        />
      )
    }

    // Render create schema view
    if (selectedItem.type === 'create-schema') {
      return (
        <CreateSchemaView 
          connection={connectionForTable}
          database={selectedItem.database!}
          onSchemaCreated={onSchemaCreated}
        />
      )
    }

    // Render appropriate content based on selected item type
    if (selectedItem.type === 'table') {
      return isEditingTable ? (
          <TableEditor
            key={`edit-${selectedItem.database}-${selectedItem.schema || 'default'}-${selectedItem.name}`}
            connectionId={connectionId}
            database={selectedItem.database!}
            tableName={selectedItem.name}
            schema={selectedItem.schema}
            databaseType={dataSource.databaseType}
            onClose={onCloseTableEditor}
          />
        ) : (
          <TableDataView 
            key={`${selectedItem.database}-${selectedItem.schema || 'default'}-${selectedItem.name}`}
            connection={connectionForTable}
            database={selectedItem.database!}
            table={selectedItem.name}
            schema={selectedItem.schema}
            onEditStructure={onEditStructure}
          />
        );
    }

    // Render view details when individual view is selected
    if (selectedItem.type === 'view') {
      // Show properties/definition only when explicitly editing
      if (isEditingView) {
        return (
          <ViewDetailsView 
            connection={connectionForTable}
            database={selectedItem.database!}
            viewName={selectedItem.name}
            schema={selectedItem.schema}
            openEditPanel={isViewEditPanelOpen}  // Use separate edit panel state
            onEditPanelClose={onEditPanelCloseView}
            onViewData={() => setIsEditingView(false)}  // Switch to data view
          />
        )
      }
      // By default, show the view data (like tables)
      return (
        <TableDataView
          connection={connectionForTable}
          database={selectedItem.database!}
          table={selectedItem.name}
          schema={selectedItem.schema}
          isReadOnly={true}  // Views are read-only
        />
      )
    }

    // Render indexes view when indexes folder is selected
    if (selectedItem.type === 'indexes-folder') {
      return (
        <IndexesView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
        />
      )
    }

    // Render tables view when tables folder is selected
    if (selectedItem.type === 'tables-folder') {
      return (
        <TablesView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onTableSelect={(tableName) => {
            // Expand tree to show the selected table
            databaseTreeRef.current?.expandToNode(selectedItem.database!, selectedItem.schema, 'tables')
            
            setSelectedItem({
              type: 'table',
              connectionId: connectionId || '',
              database: selectedItem.database!,
              schema: selectedItem.schema,
              name: tableName,
              fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.${tableName}` : `${selectedItem.database}.${tableName}`
            })
          }}
        />
      )
    }

    // Render views view when views folder is selected
    if (selectedItem.type === 'views-folder') {
      return (
        <ViewsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onViewSelect={(viewName) => {
            // Expand tree to show the selected view
            databaseTreeRef.current?.expandToNode(selectedItem.database!, selectedItem.schema, 'views')
            
            setSelectedItem({
              type: 'view',
              connectionId: connectionId || '',
              database: selectedItem.database!,
              schema: selectedItem.schema,
              name: viewName,
              fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.${viewName}` : `${selectedItem.database}.${viewName}`
            })
            setIsEditingView(false)  // Explicitly set to false to show data instead of definition
          }}
          onViewDefinition={(viewName) => {
            // Expand tree to show the selected view
            databaseTreeRef.current?.expandToNode(selectedItem.database!, selectedItem.schema, 'views')
            
            setSelectedItem({
              type: 'view',
              connectionId: connectionId || '',
              database: selectedItem.database!,
              schema: selectedItem.schema,
              name: viewName,
              fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.${viewName}` : `${selectedItem.database}.${viewName}`
            })
            setIsEditingView(true)  // Show properties view, but don't open edit panel
          }}
        />
      )
    }

    // Render functions view when functions folder is selected
    if (selectedItem.type === 'functions-folder') {
      return (
        <FunctionsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onFunctionSelect={onFunctionSelect}
        />
      )
    }

    // Render function details when individual function is selected
    if (selectedItem.type === 'function') {
      return (
        <FunctionDetailsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          functionName={selectedItem.name}
          schema={selectedItem.schema}
          functionArguments={selectedItem.arguments}
          openEditPanel={isEditingFunction}
          onEditPanelClose={onEditPanelCloseFunction}
        />
      )
    }

    // Render procedures view when procedures folder is selected
    if (selectedItem.type === 'procedures-folder') {
      return (
        <ProceduresView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onProcedureSelect={onProcedureSelect}
        />
      )
    }

    // Render procedure details when individual procedure is selected
    if (selectedItem.type === 'procedure') {
      return (
        <ProcedureDetailsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          procedureName={selectedItem.name}
          schema={selectedItem.schema}
          openEditPanel={isEditingProcedure}
          onEditPanelClose={onEditPanelCloseProcedure}
        />
      )
    }

    // Render trigger details when individual trigger is selected
    if (selectedItem.type === 'trigger') {
      return (
        <TriggerDetailsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          triggerName={selectedItem.name}
          schema={selectedItem.schema}
          openEditPanel={isEditingTrigger}
          onEditPanelClose={onEditPanelCloseTrigger}
        />
      )
    }

    // Render index details when individual index is selected
    if (selectedItem.type === 'index') {
      return (
        <IndexDetailsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          indexName={selectedItem.name}
          schema={selectedItem.schema}
          onIndexDeleted={onIndexDeleted}
        />
      )
    }

    // Render triggers view when triggers folder is selected
    if (selectedItem.type === 'triggers-folder') {
      return (
        <TriggersView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onTriggerSelect={(triggerName: string) => {
            if (connectionId) {
              // Expand tree to show the selected trigger
              databaseTreeRef.current?.expandToNode(selectedItem.database!, selectedItem.schema, 'triggers')
              setSelectedItem({
                type: 'trigger',
                connectionId: connectionId || '',
                database: selectedItem.database!,
                schema: selectedItem.schema,
                name: triggerName,
                fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.${triggerName}` : `${selectedItem.database}.${triggerName}`
              })
            }
          }}
        />
      )
    }

    // Render sequences view when sequences folder is selected
    if (selectedItem.type === 'sequences-folder') {
      return (
        <SequencesView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onSequenceSelect={(sequenceName: string) => {
            if (connectionId) {
              // Expand tree to show the selected sequence
              databaseTreeRef.current?.expandToNode(selectedItem.database!, selectedItem.schema, 'sequences')
              setSelectedItem({
                type: 'sequence',
                connectionId: connectionId || '',
                database: selectedItem.database!,
                schema: selectedItem.schema,
                name: sequenceName,
                fullPath: selectedItem.schema ? `${selectedItem.database}.${selectedItem.schema}.${sequenceName}` : `${selectedItem.database}.${sequenceName}`
              })
            }
          }}
        />
      )
    }

    // Render extensions view when extensions folder is selected
    if (selectedItem.type === 'extensions-folder') {
      return (
        <ExtensionsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
          onExtensionSelect={onExtensionSelect}
        />
      )
    }

    // Render events view when events folder is selected
    if (selectedItem.type === 'events-folder') {
      return (
        <EventsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          schema={selectedItem.schema}
        />
      )
    }


    // Render event details when individual event is selected
    if (selectedItem.type === 'event') {
      return (
        <EventDetailsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          eventName={selectedItem.name}
          schema={selectedItem.schema}
        />
      )
    }

    // Render extension details when individual extension is selected
    if (selectedItem.type === 'extension') {
      return (
        <ExtensionDetailsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          extensionName={selectedItem.name}
          schema={selectedItem.schema}
        />
      )
    }

    // Render sequence details when individual sequence is selected
    if (selectedItem.type === 'sequence') {
      return (
        <SequenceDetailsView 
          connection={connectionForTable}
          database={selectedItem.database!}
          sequenceName={selectedItem.name}
          schema={selectedItem.schema}
        />
      )
    }

    // Default content view for other types
    return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="text-2xl mr-3">
                {selectedItem.type === 'schema' && '📁'}
                {selectedItem.type === 'connection' && '🔌'}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedItem.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedItem.type.charAt(0).toUpperCase() + selectedItem.type.slice(1)}
                </p>
              </div>
            </div>
            
            {/* Table Sizes View Button */}
            {selectedItem.type === 'schema' && (
              <button type="button"
                onClick={onSetViewModeTableSizes}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                View Table Sizes
              </button>
            )}
          </div>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Type
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {selectedItem.type}
                </p>
              </div>
              
              {selectedItem.database && (
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Database
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {selectedItem.database}
                  </p>
                </div>
              )}
              
              {selectedItem.schema && (
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Schema
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {selectedItem.schema}
                  </p>
                </div>
              )}
              
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Full Path
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1 font-mono">
                  {selectedItem.fullPath || selectedItem.name}
                </p>
              </div>
            </div>
            
            {selectedItem.type === 'schema' && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 Expand the database in the tree view to see all available objects: tables, views, functions, procedures, triggers, indexes, {dataSource.databaseType === 'PostgreSQL' ? 'sequences, extensions, ' : ''}
                  {dataSource.databaseType === 'MySQL' ? 'events, ' : ''}and users.
                </p>
              </div>
            )}
            
            {selectedItem.type === 'schema' && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  💡 Expand the schema in the tree view to see its tables, views, functions, and procedures.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full">
      <Allotment>
        {/* Left Panel - Database Tree */}
        <Allotment.Pane minSize={250} maxSize={500} preferredSize={350}>
          <div className="h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {dataSource.displayName}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {dataSource.databaseType} • {dataSource.host}:{dataSource.port}
                    </p>
                  </div>
                </div>
                
                {/* View Mode Toggle */}
                {viewMode === 'table-sizes' && (
                  <button type="button"
                    onClick={onSetViewModeExplorer}
                    className="flex items-center px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Explorer
                  </button>
                )}
              </div>
            </div>
            
            {/* Tree View */}
            <div className="flex-1 overflow-hidden">
              <DatabaseTree
                ref={databaseTreeRef}
                connectionId={connectionId}
                dataSource={dataSource}
                databaseType={dataSource.databaseType}
                selectedItem={databaseTreeSelectedItem}
                onItemSelect={handleItemSelect}
                onContextMenuAction={handleContextMenuAction}
                clearMultiSelection={clearMultiSelection}
                analyzedTables={analyzedTables}
              />
            </div>
          </div>
        </Allotment.Pane>

        {/* Right Panel - Content View */}
        <Allotment.Pane>
          <div className="h-full bg-gray-50 dark:bg-gray-900">
            {renderContentView()}
          </div>
        </Allotment.Pane>
      </Allotment>

      {/* Modals */}
      {showDiagramModal && contextMenuTarget && (
        <DatabaseDiagramModal 
          isOpen={showDiagramModal}
          onClose={onCloseDiagramModal}
          connectionId={connectionId}
          database={contextMenuTarget.database}
          schema={contextMenuTarget.schema}
          databaseType={dataSource.databaseType}
        />
      )}

      {showPlaceholderModal && placeholderModalData && (
        <PlaceholderModal 
          isOpen={showPlaceholderModal}
          onClose={onClosePlaceholderModal}
          title={placeholderModalData.title}
          action={placeholderModalData.action}
          data={placeholderModalData.data}
        />
      )}

      {/* Export Data Modal */}
      {showExportModal && exportModalData && (
        <ExportModal
          isOpen={showExportModal}
          onClose={onCloseExportModal}
          onExport={onExportData}
          columns={exportModalData.columns}
          totalRows={exportModalData.totalRows}
        />
      )}

      {/* Drop Confirmation Dialog */}
      {dropData && (
        <DropConfirmDialog
          isOpen={isDropDialogOpen}
          onClose={cancelDrop}
          onConfirm={confirmDrop}
          type={dropData.type}
          name={dropData.name}
          database={dropData.database}
          schema={dropData.schema}
          isLoading={isDropLoading}
        />
      )}

      {/* Bulk Drop Confirmation Dialog */}
      <BulkDropConfirmDialog
        isOpen={isBulkDropDialogOpen}
        onClose={cancelBulkDrop}
        onConfirm={confirmBulkDrop}
        bulkDropData={bulkDropData}
        isLoading={isBulkDropLoading}
        progress={progress}
      />
    </div>
  )
}
